/**
 * BaseEntityProperties - Entity의 기본 속성을 관리하는 기본 클래스
 * 모든 Entity 타입이 공유하는 속성과 메서드를 정의합니다.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ICanvasEngineEntityController, ICanvasEngineEntity } from '../../types/interface'
import { CLOUD_RADIUS, DASH_GAP, DASH_LENGTH, SCALE_BASE_SIZE, SHAPES } from '../../const'
import { isApp } from '../../../..'
import type { TripodShapePoints } from '@/app/data/type'
import { DirectionStateManager } from '../types/direction/stateManager'
import {
  updateDirectionElements,
  type DirectionBalloonClip,
} from '../types/direction/stateManager/helper'
import type {
  IArrow,
  ICircle,
  IEllipse,
  IGroup,
  ILine,
  INode,
  IPath,
  IPathContext,
  IRenderer,
  ISelectionHandles,
  IShape,
} from '../../backend/IRenderer'
import type { GroupConfig, SelectionHandlesConfig, Vector2d } from '../../backend/types'
import { isArrowNode, isEllipseNode, isLineNode, isRectNode } from '../../backend/nodeGuards'

/**
 * Entity의 기본 속성을 관리하는 클래스
 * 엔티티는 렌더러 노드를 상속하지 않고 루트 그룹 핸들(`node`)을 가진다.
 */
export class BaseEntityProperties implements ICanvasEngineEntity {
  /** 엔티티 루트 노드 — 변환·표시·이벤트·자식 조립은 전부 이것으로. 엔진(src/lib/CanvasEngine) 밖에서는 쓰지 않는다 */
  readonly node: IGroup
  isSelected: boolean = false
  canvasEngine: ICanvasEngineEntityController
  entityType: SHAPES = SHAPES.POINT
  isBackgroundEntity: boolean = false

  // 보수완료 되었는지, text 대신 checkIcon 사용할지 여부
  protected isChecked = false
  protected __entityInteractionMode: boolean = true
  protected __id: string = ''
  protected __layerId: string = ''
  protected __template: 'default' | 'previous' = 'default'

  // Entity가 Line 형인 경우, 해당 값 사용
  protected __line: ILine | undefined
  // Entity가 Line형이 아닌 경우, 해당 값 사용
  protected __shape: IShape | undefined
  protected __shape_wrapper: IGroup | undefined
  // 브러시 (line의 묶음)
  protected __brush: IGroup | undefined
  // 화살표 그룹
  protected __directionShape: IGroup | undefined
  protected __directionPoints: number[] = []
  protected __lineTransformerStartHandle: ICircle | undefined
  protected __lineTransformerEndHandle: ICircle | undefined
  protected __shapeTransformer: ISelectionHandles | undefined
  protected __color: string = '#000000'
  // CanvasEngine에서 제공하는 인덱스를 사용합니다.
  protected __useIndex: string = ''
  protected __enableIndex: boolean = false

  // 개별 채우기 설정 (undefined면 전역 설정 따름)
  protected __individualFillSetting: boolean | undefined = undefined
  // 개별 도형 스타일 설정 (undefined면 전역 설정 따름)
  protected __individualShapeTypeSetting: 'line' | 'dash' | 'cloud' | undefined = undefined
  // 개별 점 도형 스타일 설정 (undefined 또는 'dot'은 동그라미, 'arrow'는 화살표 머리)
  protected __individualDotTypeSetting: 'dot' | 'arrow' | undefined = undefined
  // POINT 화살표 머리 (dotType === 'arrow'일 때만 visible) — 방향도형과 동일한 화살표(Arrow) 사용
  protected __pointArrowHead: IArrow | undefined
  // 개별 벌룬 색상 설정 (undefined면 전역 설정 따름)
  protected __balloonFontColor: string | undefined = undefined
  protected __balloonFillColor: string | undefined = undefined

  // TRIPOD 전용 설정
  protected __tripodShapePoints: TripodShapePoints | undefined = undefined
  protected __tailStyle: 'arrow' | 'dot' | undefined = undefined
  // TRIPOD transformer 대상 그룹 (balloon 제외)
  protected __tripod_transform_group: IGroup | undefined = undefined
  // 방향도형 드래그 상태 관리
  protected __directionStateManager = new DirectionStateManager()

  // 모든 shape관련 스타일은 기본값에 유저가 설정가능한 배율: 0.4, 0.7, 1, 2.2, 3 을 곱하여 사용
  // shape 관련 상수
  protected originBalloonSize: number = 15 // 벌룬의 기본 크기 (반지름)
  protected originBalloonThickness: number = 1 // 벌룬 테두리의 기본 굵기
  protected originBalloonFontSize: number = 20 // 벌룬 글씨의 기본 크기
  protected originConnectedLineWidth: number = 1 // 연결선의 기본 굵기
  protected originPointSize: number = 3 // 점의 크기 (지름)
  protected originAreaLineWidth: number = 1 // 영역 선 굵기

  // 전역 및 floorplan에서 설정된 값
  protected __balloonScale: number = 1
  protected __connectedLineThicknessScale: number = 1
  protected __balloonFontScale: number = 1
  protected __pointShapeScale: number = 1
  protected __areaBorderThicknessScale: number = 1

  // Cloud border 관련
  protected __cloudBorder: IPath | undefined

  // 변화된 스케일에 따라 계산된 값
  get calculatedBalloonSize(): number {
    return this.originBalloonSize * 2 * this.__balloonScale
  }
  get calculatedBalloonThickness(): number {
    return this.originBalloonThickness * this.__balloonScale
  }
  get calculatedBalloonFontSize(): number {
    return this.originBalloonFontSize * this.__balloonFontScale
  }
  get calculatedConnectedLineWidth(): number {
    return this.originConnectedLineWidth * this.__connectedLineThicknessScale
  }
  get calculatedPointSize(): number {
    return this.originPointSize * this.__pointShapeScale
  }
  get calculatedAreaLineWidth(): number {
    return this.originAreaLineWidth * this.__areaBorderThicknessScale
  }
  // 화살표 헤드 크기 (방향도형/점도형/그룹꼬리 공통)
  // pointShapeScale의 최소(0.5)에서 multiplier 1.0, 최대(5.0)에서 1.5로 선형 보간
  get calculatedArrowHeadSize(): number {
    const minScale = 0.5
    const maxScale = 5.0
    const clamped = Math.max(minScale, Math.min(maxScale, this.__pointShapeScale))
    const multiplier = 1.0 + 0.5 * ((clamped - minScale) / (maxScale - minScale))
    return this.calculatedPointSize * multiplier
  }

  // Konva cache()는 가시 영역(getClientRect) 크기로 hit canvas를 만들어 히트 판정을 대체하므로,
  // hitFunc/hitStrokeWidth가 가시 영역 밖에 만드는 확장 터치 영역이 잘리지 않도록 캐시 여백(px)을 반환
  getCacheHitOffset(): number {
    // pointEntity hitFunc: 히트 반경 = 가시 반경 × 8 → 가시 영역 밖 여분은 가시 반경의 7배
    // 상한 60px — offset은 캐시 비트맵을 사방으로 키우므로, 큰 점 스케일에서
    // 메모리가 과대해지지 않도록 제한 (60px이면 터치 여백으로 충분)
    if (this.isPoint()) {
      const shape = this.__shape
      const radius = isEllipseNode(shape) ? shape.radiusX() : this.calculatedPointSize
      return Math.ceil(Math.min(Math.max(radius * 7, 30), 60))
    }
    // rectEntity hitFunc: 외곽 +20px 패딩 사각형을 hitStrokeWidth 20으로 stroke → 가시 영역 밖 최대 30px
    // 그 외 타입(hitStrokeWidth ≤ 20)도 30px이면 충분히 커버됨
    return 30
  }

  protected onIndexChanged(_index: string): void {}

  /** 렌더러 — 내부 노드는 이 팩토리로 만든다 */
  protected get renderer(): IRenderer {
    return this.canvasEngine.getRenderer()
  }

  /**
   * 선택 핸들(Transformer) 생성 — 렌더러 capability(Konva 구현체는 항상 제공).
   * 선택 핸들러(*SelectionHandler)와 TripodSelectionMixin이 같이 쓰므로 공통 베이스에 둔다.
   */
  public createSelectionHandles(cfg: SelectionHandlesConfig, nodes: INode[]): ISelectionHandles {
    const factory = this.renderer.capabilities.selectionHandles
    if (!factory) throw new Error('선택 핸들을 지원하지 않는 렌더러입니다.')
    return factory(cfg, nodes)
  }

  get isDraggable(): boolean {
    return this.node.draggable()
  }
  set isDraggable(value: boolean) {
    this.node.draggable(value)
  }

  get avaliableShape(): IShape | IGroup {
    if (this.__brush) return this.__brush
    if (this.__line) return this.__line
    if (this.__shape) return this.__shape
    if (this.__shape_wrapper) return this.__shape_wrapper
    throw new Error('사용 가능한 모양이 없습니다.')
  }

  /**
   * BaseEntityProperties 생성자
   * @param parent - 부모 CanvasEngine 컨트롤러
   * @param config - 루트 그룹 생성 설정
   */
  constructor(parent: ICanvasEngineEntityController, config: GroupConfig | undefined) {
    this.canvasEngine = parent
    // entityRoot: 캐시된 엔티티의 listening/visible 토글 시 하위 노드 캐시까지 무효화하는 구현체 그룹(KonvaEntityGroup)
    this.node = parent.getRenderer().createGroup({ ...config, entityRoot: true })
  }

  /**
   * 인덱스 활성화 여부 확인
   * @returns 인덱스 활성화 여부
   */
  isEnabledIndex(): boolean {
    return this.__enableIndex
  }

  /**
   * 인덱스 활성화
   */
  enableIndex(): void {
    if (this.isBackgroundEntity) return
    if (this.isBrush()) return
    this.__enableIndex = true
  }

  /**
   * 인덱스 비활성화
   */
  disableIndex(): void {
    this.__enableIndex = false
    this.__useIndex = ''
    this.onIndexChanged(this.__useIndex)
  }

  /**
   * 인덱스 적용
   */
  applyIndex(): void {
    if (!this.__enableIndex) return
    this.__useIndex = this.canvasEngine.useIndex(this.__id)
    this.onIndexChanged(this.__useIndex)
  }

  /**
   * 마지막 인덱스 반환
   * @returns 마지막 인덱스
   */
  getLastIndex(): string {
    return (Number(this.__useIndex) + 1).toString()
  }

  /**
   * Entity ID 반환
   * @returns Entity ID
   */
  getId(): string {
    return this.__id
  }

  /**
   * Entity ID 설정
   * @param id - 설정할 ID
   */
  setId(id: string): void {
    this.__id = id
    // 상속 시절 Konva `id()`는 이 메서드(getId/setId 오버라이드)를 거쳐 __id를 읽고 썼다 → 루트 노드 id도 같은 값으로 유지
    // (EventManager 이벤트 키 `node.id() + key`, 브러시 제스처 스냅샷 시그니처가 노드 id를 쓴다)
    this.node.id(id)
  }

  // ---- 경계 API: 바깥이 Konva 노드 메서드(listening/x/y/getLayer/isCached…)를 직접 부르지 않게 하는 래퍼 ----
  // 이름은 Konva 자동 생성 getter/setter(setListening/getPosition 등)와 겹치지 않게 짓는다
  // (Konva의 listening(v)는 내부에서 this.setListening(v)를 호출하므로 같은 이름이면 무한 재귀).

  /** 포인터 이벤트(hit 테스트) 수신 여부 */
  setPointerListening(enabled: boolean): void {
    this.node.listening(enabled)
  }

  /**
   * 비트맵 캐시를 무효화한다(다음 batchDraw가 라이브 드로우로 최신 상태를 그림).
   * 즉시 재캐시하지 않는다 — 재캐시는 배치 경로(refreshCache/refreshAsyncCache/deferredCacheEntities)가 수행.
   */
  invalidateCache(): void {
    if (this.renderer.hints.isStatic(this.node)) this.renderer.hints.unmarkStatic(this.node)
  }

  /** 이 엔티티가 속한 레이어를 다시 그린다(선택 중이면 systemLayer일 수 있어 엔진 redraw가 아니라 자신의 레이어) */
  redraw(): void {
    this.node.getLayer()?.batchDraw()
  }

  /**
   * Entity 상호작용 모드 반환
   * @returns 상호작용 모드
   */
  getEntityInteractionMode(): boolean {
    return this.__entityInteractionMode
  }

  /**
   * Entity 상호작용 모드 설정
   * @param mode - 상호작용 모드
   */
  setEntityInteractionMode(mode: boolean): void {
    this.__entityInteractionMode = mode
    this.isDraggable = mode
    if (mode) {
      this.enableShapeDrag()
    } else {
      this.disableShapeDrag()
    }
  }

  /**
   * Shape 드래그 활성화
   */
  enableShapeDrag(): void {
    if (this.__shape_wrapper) this.__shape_wrapper.draggable(true)
    else if (this.__shape) this.__shape.draggable(true)
    else if (this.__line) this.__line.draggable(true)
    else if (this.__brush) this.__brush.draggable(true)
  }

  /**
   * Shape 드래그 비활성화
   */
  disableShapeDrag(): void {
    if (this.__shape_wrapper) this.__shape_wrapper.draggable(false)
    else if (this.__shape) this.__shape.draggable(false)
    else if (this.__line) this.__line.draggable(false)
    else if (this.__brush) this.__brush.draggable(false)
  }

  /**
   * 상대 X 좌표 반환
   * @returns 상대 X 좌표
   */
  getRelativeX(): number {
    if (this.__shape) return this.node.x()
    else if (this.__line) return this.__line.points()[0]
    else return this.node.x()
  }

  /**
   * 상대 Y 좌표 반환
   * @returns 상대 Y 좌표
   */
  getRelativeY(): number {
    if (this.__shape) return this.node.y()
    else if (this.__line) return this.__line.points()[1]
    else return this.node.y()
  }

  /**
   * Line 타입인지 확인
   * @returns Line 타입 여부
   */
  isLine(): boolean {
    return (
      this.entityType === SHAPES.LINE ||
      this.entityType === SHAPES.ARROW ||
      this.entityType === SHAPES.DOUBLE_ARROW
    )
  }

  /**
   * Brush 타입인지 확인
   * @returns Brush 타입 여부
   */
  isBrush(): boolean {
    return this.entityType === SHAPES.BRUSH
  }

  /**
   * Direction 타입인지 확인
   * @returns Direction 타입 여부
   */
  isDirection(): boolean {
    return this.entityType === SHAPES.DIRECTION
  }

  /**
   * Ellipse 타입인지 확인
   * @returns Ellipse 타입 여부
   */
  isEllipse(): boolean {
    return isEllipseNode(this.__shape)
  }

  /**
   * Rect 타입인지 확인
   * @returns Rect 타입 여부
   */
  isRect(): boolean {
    return isRectNode(this.__shape)
  }

  /**
   * Point 타입인지 확인
   * @returns Point 타입 여부
   */
  isPoint(): boolean {
    return this.entityType === SHAPES.POINT
  }

  /**
   * Tripod 타입인지 확인
   * @returns Tripod 타입 여부
   */
  isTripod(): boolean {
    return this.entityType === SHAPES.TRIPOD
  }

  /**
   * Opacity 상태를 현재 Property에 따라서 적용합니다.
   */
  updateStatus(): void {
    this.node.opacity(this.isChecked ? 0.5 : 1)
  }

  // opacity 강제 설정 관련
  protected __forceOpacity: number = 1
  protected __opacityStack: number[] = []
  protected __isOpacityForced: boolean = false

  /**
   * Opacity 강제 설정
   * @param opacity - 설정할 opacity
   */
  forceOpacity(opacity: number, skipDraw: boolean = false): void {
    if (this.__isOpacityForced) return

    this.__opacityStack.push(this.node.opacity())
    this.node.opacity(opacity)
    this.__isOpacityForced = true

    // 개별 렌더링을 건너뛰고 나중에 배치로 처리
    if (!skipDraw) {
      this.node.getLayer()?.batchDraw()
    }
  }
  /**
   * Opacity 복원
   */
  restoreOpacity(skipDraw: boolean = false): void {
    if (!this.__isOpacityForced) return

    const previousOpacity = this.__opacityStack.pop()
    if (previousOpacity !== undefined) this.node.opacity(previousOpacity)
    this.__isOpacityForced = false

    if (!skipDraw) {
      this.node.getLayer()?.batchDraw()
    }
  }

  /**
   * Checked 상태 설정
   * @param checked - checked 상태
   */
  setChecked(checked: boolean): void {
    this.isChecked = checked
    this.updateStatus()
  }

  /**
   * Checked 상태 반환
   * @returns checked 상태
   */
  getChecked(): boolean {
    return this.isChecked
  }

  /**
   * 중심점 반환
   * @returns 중심점 좌표
   */
  getCenterPoint(): Vector2d {
    if (this.__line) {
      return {
        x: (this.__line.points()[0] + this.__line.points()[2]) / 2,
        y: (this.__line.points()[1] + this.__line.points()[3]) / 2,
      }
    } else if (isEllipseNode(this.__shape)) {
      return { x: this.node.x(), y: this.node.y() }
    } else if (isRectNode(this.__shape)) {
      return {
        x: this.node.x() + this.__shape.width() / 2,
        y: this.node.y() + this.__shape.height() / 2,
      }
    } else if (this.__brush) {
      return this.getBrushCenterPoint()
    } else if (this.isDirection()) {
      return this.getDirectionCenterPoint()
    } else if (this.isTripod()) {
      return this.getTripodCenterPoint()
    }
    throw new Error('getCenterPoint()을 지원하지 않는 도형입니다.')
  }

  /**
   * Brush 중심점 계산
   */
  protected getBrushCenterPoint(): Vector2d {
    const allLines = this.__brush!.find<ILine>('Line')
    const visibleLines = allLines.filter(
      (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
    )

    if (visibleLines.length === 0) return { x: this.node.x(), y: this.node.y() }

    let minX = Number.MAX_VALUE
    let minY = Number.MAX_VALUE
    let maxX = Number.MIN_VALUE
    let maxY = Number.MIN_VALUE

    for (const line of visibleLines) {
      const points = line.points()
      for (let i = 0; i < points.length; i += 2) {
        const x = points[i]
        const y = points[i + 1]
        minX = Math.min(minX, x)
        minY = Math.min(minY, y)
        maxX = Math.max(maxX, x)
        maxY = Math.max(maxY, y)
      }
    }

    return {
      x: minX + (maxX - minX) / 2,
      y: minY + (maxY - minY) / 2,
    }
  }

  /**
   * Direction 중심점 계산
   */
  protected getDirectionCenterPoint(): Vector2d {
    if (this.__shape) {
      return this.__shape.position()
    } else {
      if (this.__directionPoints.length >= 2) {
        return { x: this.__directionPoints[0], y: this.__directionPoints[1] }
      }
    }
    throw new Error('Direction 도형의 중심점을 계산할 수 없습니다.')
  }

  /**
   * Tripod 중심점 계산
   */
  protected getTripodCenterPoint(): Vector2d {
    const tripodData = this.getTripodShapePoints()
    if (!tripodData) {
      throw new Error('TRIPOD 데이터가 없습니다.')
    }

    const allPoints: number[] = []
    allPoints.push(tripodData.mainAxisPoint1[0], tripodData.mainAxisPoint1[1])
    allPoints.push(tripodData.mainAxisPoint2[0], tripodData.mainAxisPoint2[1])
    for (const dot of tripodData.dots) {
      allPoints.push(dot[0], dot[1])
    }

    let minX = Number.MAX_VALUE
    let minY = Number.MAX_VALUE
    let maxX = Number.MIN_VALUE
    let maxY = Number.MIN_VALUE

    for (let i = 0; i < allPoints.length; i += 2) {
      const x = allPoints[i]
      const y = allPoints[i + 1]
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x)
      maxY = Math.max(maxY, y)
    }

    const centerX = minX + (maxX - minX) / 2
    const centerY = minY + (maxY - minY) / 2

    return {
      x: centerX - this.node.x(),
      y: centerY - this.node.y(),
    }
  }

  /**
   * 선택 해제
   */
  blur(): void {
    if (this.__mouseHover) {
      this.mouseLeave(new MouseEvent('mouseleave'))
    }

    this.isSelected = false
    this.__mouseHover = false
    this.isDraggable = false
    this.disableShapeDrag()
  }

  /**
   * 선택
   */
  select(): void {
    if (!this.getEntityInteractionMode()) return
    if (this.isSelected) return
    this.node.moveToTop()
    this.isSelected = true
    this.renderer.stage.container().style.cursor = 'move'
  }

  /**
   * 회전 설정
   * @param rotate - 회전 각도
   */
  setRotate(rotate: number): void {
    if (this.isLine()) this.__line!.rotate(rotate)
    else if (this.isBrush() && this.__brush) this.__brush.rotate(rotate)
    else this.__shape!.rotate(rotate)
  }

  /**
   * Stroke 설정
   * @param stroke - stroke 색상
   */
  setStroke(stroke: string): void {
    if (this.isLine()) {
      this.__line!.stroke(stroke)
    } else if (this.isBrush()) {
      if (this.__brush) {
        const allLines = this.__brush.find<ILine>('Line')
        const visibleLines = allLines.filter(
          (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
        )
        visibleLines.forEach((line: ILine) => line.stroke(stroke))
      }
    } else if (this.isDirection()) {
      if (this.__shape_wrapper) {
        const allLines = this.__shape_wrapper.find<ILine>('Line')
        const allArrows = this.__shape_wrapper.find<IArrow>('Arrow')
        allLines.forEach((line: ILine) => line.stroke(stroke))
        allArrows.forEach((arrow: IArrow) => {
          arrow.stroke(stroke)
          arrow.fill(stroke)
        })
      }
    } else if (this.__directionShape) {
      const arrowChildren = this.__directionShape.getChildren()
      arrowChildren.forEach((child: INode) => {
        if (isLineNode(child)) {
          child.stroke(stroke)
          if (isArrowNode(child)) {
            child.fill(stroke)
          }
        }
      })
    } else if (this.__shape && typeof this.__shape.stroke === 'function') {
      this.__shape.stroke(stroke)
    }
  }

  /**
   * 회전 각도 반환
   * @returns 회전 각도
   */
  getRotate(): number {
    if (this.isLine()) return this.__line?.rotation() ?? 0
    else if (this.isBrush()) return this.__brush?.rotation() ?? 0
    else if (this.isDirection()) return 0
    else if (this.__shape) return this.__shape.rotation()
    throw new Error('rotation()을 지원하지 않는 도형입니다.')
  }

  /**
   * Points 설정
   * @param points - 점 배열
   */
  setPoints(points: number[]): void {
    if (this.isLine()) {
      this.__line!.points(points)
    } else if (this.isBrush() && this.__brush) {
      const allLines = this.__brush.find<ILine>('Line')
      const visibleLines = allLines.filter(
        (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
      )
      if (visibleLines.length > 0) visibleLines[0].points(points)
    } else if (this.isDirection()) {
      this.__directionPoints = points
      if (this.node.getStage()) this.renderer.requestDraw()
    }
  }

  /**
   * Line 설정
   * @param line - Line 객체
   */
  setLine(line: ILine): void {
    if (isLineNode(line)) {
      this.__line = line
      return
    }
    throw new Error('line()을 지원하지 않는 도형입니다.')
  }

  /**
   * Points 반환
   * @returns 점 배열
   */
  getPoints(): number[] {
    if (this.isLine()) {
      return this.__line?.points() ?? []
    } else if (this.isBrush() && this.__brush) {
      const allLines = this.__brush.find<ILine>('Line')
      const visibleLines = allLines.filter(
        (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
      )
      return visibleLines.length > 0 ? visibleLines[0].points() : []
    } else if (this.isDirection()) {
      return this.__directionPoints
    }
    return []
  }

  /**
   * Stroke 색상 반환
   * @returns Stroke 색상
   */
  getStroke(): string | CanvasGradient | undefined {
    if (this.isLine()) {
      return this.__line?.stroke()
    } else if (this.isBrush() && this.__brush) {
      const allLines = this.__brush.find<ILine>('Line')
      const visibleLines = allLines.filter(
        (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
      )
      return visibleLines.length > 0 ? visibleLines[0].stroke() : undefined
    } else if (this.isDirection() && this.__directionShape) {
      const allLines = this.__directionShape.find<ILine>('Line')
      return allLines.length > 0 ? allLines[0].stroke() : undefined
    } else if (this.__shape && typeof this.__shape.stroke === 'function') {
      return this.__shape.stroke()
    }
    throw new Error('stroke()을 지원하지 않는 도형입니다.')
  }

  /**
   * Stroke 두께 반환
   * @returns Stroke 두께
   */
  getStrokeWidth(): number | undefined {
    if (this.isLine()) {
      return this.__line?.strokeWidth()
    } else if (this.isBrush() && this.__brush) {
      const allLines = this.__brush.find<ILine>('Line')
      const visibleLines = allLines.filter(
        (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
      )
      return visibleLines.length > 0 ? visibleLines[0].strokeWidth() : undefined
    } else if (this.isDirection() && this.__directionShape) {
      const allLines = this.__directionShape.find<ILine>('Line')
      return allLines.length > 0 ? allLines[0].strokeWidth() : undefined
    } else if (this.__shape && typeof this.__shape.strokeWidth === 'function') {
      return this.__shape.strokeWidth()
    }
    throw new Error('strokeWidth()을 지원하지 않는 도형입니다.')
  }

  /**
   * Fill 색상 반환
   * @returns Fill 색상
   */
  getFill(): string | CanvasGradient {
    if (this.__shape) {
      return this.__shape.fill()
    }
    throw new Error('fill()을 지원하지 않는 도형입니다.')
  }

  /**
   * Stroke 두께 설정
   * @param strokeWidth - Stroke 두께
   */
  setStrokeWidth(strokeWidth: number): void {
    if (this.isLine()) this.__line!.strokeWidth(strokeWidth)
    else if (this.isBrush()) return
    else if (this.isDirection() && this.__directionShape) {
      const allLines = this.__directionShape.find<ILine>('Line')
      const allArrows = this.__directionShape.find<IArrow>('Arrow')
      allLines.forEach((line: ILine) => line.strokeWidth(strokeWidth))
      allArrows.forEach((arrow: IArrow) => arrow.strokeWidth(strokeWidth))
    } else if (this.__shape && typeof this.__shape.strokeWidth === 'function')
      this.__shape.strokeWidth(strokeWidth)
  }

  /**
   * Shape 너비 반환
   * @returns Shape 너비
   */
  getShapeWidth(): number {
    if (this.__shape) return this.__shape.width()
    if (this.__directionShape) {
      const bbox = this.__directionShape.getClientRect()
      return bbox.width
    }
    throw new Error('지원하지 않는 도형입니다.')
  }

  /**
   * Shape 높이 반환
   * @returns Shape 높이
   */
  getShapeHeight(): number {
    if (this.__shape) return this.__shape.height()
    if (this.__directionShape) {
      const bbox = this.__directionShape.getClientRect()
      return bbox.height
    }
    throw new Error('지원하지 않는 도형입니다.')
  }

  /**
   * Shape 위치 설정
   * @param x - X 좌표
   * @param y - Y 좌표
   */
  setShapePosition(x: number, y: number): void {
    if (this.__shape) {
      this.__shape.x(x)
      this.__shape.y(y)
    }
  }

  /**
   * Line 위치 설정
   * @param x - X 좌표
   * @param y - Y 좌표
   */
  setLinePosition(x: number, y: number): void {
    if (this.__line) {
      this.__line.x(x)
      this.__line.y(y)
    } else if (this.__brush) {
      this.__brush.x(x)
      this.__brush.y(y)
    }
  }

  /**
   * Entity 위치 설정
   * @param x - X 좌표
   * @param y - Y 좌표
   */
  setEntityPosition(x: number, y: number): void {
    this.node.x(x)
    this.node.y(y)
  }

  /** 엔티티(그룹) 위치. setEntityPosition의 짝 */
  getEntityPosition(): Vector2d {
    return { x: this.node.x(), y: this.node.y() }
  }

  /**
   * 색상 변경
   * @param color - 색상
   */
  changeColor(color: string): void {
    this.__color = color
    if (this.isDirection()) {
      if (this.__directionShape) {
        const allLines = this.__directionShape.find<ILine>('Line')
        const allArrows = this.__directionShape.find<IArrow>('Arrow')
        allLines.forEach((line: ILine) => line.stroke(color))
        allArrows.forEach((arrow: IArrow) => {
          arrow.stroke(color)
          arrow.fill(color)
        })
      }
    } else if (this.isRect() && this.__cloudBorder) {
      this.createCloudBorder()
    } else if (this.__shape && typeof this.__shape.stroke === 'function') {
      this.__shape.stroke(color)
      if (this.isPoint() && typeof this.__shape.fill === 'function') {
        this.__shape.fill(color)
      }
      // POINT가 화살표 모드인 경우 화살표 머리 색도 동기화
      if (this.isPoint() && this.__pointArrowHead) {
        this.__pointArrowHead.fill(color)
      }
    } else if (this.__line) {
      this.__line.stroke(color)
    } else if (this.__brush) {
      const allLines = this.__brush.find<ILine>('Line')
      const visibleLines = allLines.filter(
        (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
      )
      visibleLines.forEach((line: ILine) => line.stroke(color))
    }
  }

  /**
   * 색상 반환
   * @returns 색상
   */
  getColor(): string {
    return this.__color
  }

  /**
   * TripodShapePoints 반환
   * @returns TripodShapePoints
   */
  getTripodShapePoints(): TripodShapePoints | undefined {
    return this.__tripodShapePoints
  }

  /**
   * TripodShapePoints 설정
   * @param tripodShapePoints - TripodShapePoints
   */
  setTripodShapePoints(tripodShapePoints: TripodShapePoints): void {
    this.__tripodShapePoints = tripodShapePoints
  }

  /**
   * Shape 변환 처리
   */
  transformShape(): void {
    switch (this.entityType) {
      case SHAPES.RECT: {
        // RECT는 벌룬 고정처리가 필요해서 하위 클래스에서 오버라이드
        break
      }
      case SHAPES.POINT:
      case SHAPES.ELLIPSE: {
        const ellipse = this.__shape as IEllipse
        const newX = ellipse.radiusX() * ellipse.scaleX()
        const newY = ellipse.radiusY() * ellipse.scaleY()

        // 0/극소 스케일 프레임은 무시 (멀티터치로 0으로 클램프된 프레임 흡수 방지)
        if (newX > 0.25) ellipse.radiusX(newX)
        if (newY > 0.25) ellipse.radiusY(newY)

        if (this.entityType === SHAPES.ELLIPSE) {
          this.updateFillPatternOnResize()
        }

        ellipse.scaleX(1)
        ellipse.scaleY(1)

        this.node.x(ellipse.x() + this.node.x())
        this.node.y(ellipse.y() + this.node.y())
        ellipse.x(0)
        ellipse.y(0)
        break
      }
      case SHAPES.LINE:
      case SHAPES.DIRECTION:
      case SHAPES.DOUBLE_ARROW:
      case SHAPES.TRIPOD:
        break
    }
  }

  /**
   * Brush Shape 변환 처리
   */
  transformBrushShape(): void {
    if (!this.__brush) return

    const brushX = this.__brush.x()
    const brushY = this.__brush.y()
    const brushScaleX = this.__brush.scaleX()
    const brushScaleY = this.__brush.scaleY()
    const brushRotation = this.__brush.rotation()

    if (
      brushX !== 0 ||
      brushY !== 0 ||
      brushScaleX !== 1 ||
      brushScaleY !== 1 ||
      brushRotation !== 0
    ) {
      const allLines = this.__brush.find<ILine>('Line')

      allLines.forEach((line: ILine) => {
        const points = [...line.points()]
        const transformedPoints: number[] = []

        for (let i = 0; i < points.length; i += 2) {
          let x = points[i]
          let y = points[i + 1]

          x *= brushScaleX
          y *= brushScaleY

          if (brushRotation !== 0) {
            const cos = Math.cos((brushRotation * Math.PI) / 180)
            const sin = Math.sin((brushRotation * Math.PI) / 180)
            const rotatedX = x * cos - y * sin
            const rotatedY = x * sin + y * cos
            x = rotatedX
            y = rotatedY
          }

          x += brushX
          y += brushY

          transformedPoints.push(x)
          transformedPoints.push(y)
        }

        line.points(transformedPoints)
      })

      this.__brush.x(0)
      this.__brush.y(0)
      this.__brush.scaleX(1)
      this.__brush.scaleY(1)
      this.__brush.rotation(0)
    }
  }

  /**
   * Point 숨기기
   */
  hidePoint(): void {
    if (this.isPoint()) {
      this.__shape?.opacity(0)
      this.__pointArrowHead?.visible(false)
    }
  }

  /**
   * Point 보이기
   */
  showPoint(): void {
    if (this.isPoint()) {
      // 화살표 모드는 동그라미를 다시 보이게 하지 않음
      if (this.__individualDotTypeSetting === 'arrow') {
        this.__pointArrowHead?.visible(true)
        this.updatePointArrowHeadGeometry()
        return
      }
      this.__shape?.opacity(1)
    }
  }

  // 개별 설정 관련 메서드들
  setIndividualFillSetting(value: boolean | undefined): void {
    this.__individualFillSetting = value
  }

  getIndividualFillSetting(): boolean | undefined {
    return this.__individualFillSetting
  }

  hasIndividualFillSetting(): boolean {
    return this.__individualFillSetting !== undefined
  }

  setIndividualShapeTypeSetting(value: 'line' | 'dash' | 'cloud' | undefined): void {
    this.__individualShapeTypeSetting = value
  }

  getIndividualShapeTypeSetting(): 'line' | 'dash' | 'cloud' | undefined {
    return this.__individualShapeTypeSetting
  }

  hasIndividualShapeTypeSetting(): boolean {
    return this.__individualShapeTypeSetting !== undefined
  }

  setIndividualDotTypeSetting(value: 'dot' | 'arrow' | undefined): void {
    this.__individualDotTypeSetting = value
  }

  getIndividualDotTypeSetting(): 'dot' | 'arrow' | undefined {
    return this.__individualDotTypeSetting
  }

  hasIndividualDotTypeSetting(): boolean {
    return this.__individualDotTypeSetting !== undefined
  }

  getIndividualBalloonFontColor(): string | undefined {
    return this.__balloonFontColor
  }

  getIndividualBalloonFillColor(): string | undefined {
    return this.__balloonFillColor
  }

  setIndividualBalloonFontColor(color: string | undefined): void {
    this.__balloonFontColor = color
  }

  setIndividualBalloonFillColor(color: string | undefined): void {
    this.__balloonFillColor = color
  }

  hasIndividualBalloonFontColor(): boolean {
    return this.__balloonFontColor !== undefined
  }

  hasIndividualBalloonFillColor(): boolean {
    return this.__balloonFillColor !== undefined
  }

  getIndividualTailStyle(): 'arrow' | 'dot' | undefined {
    return this.__tailStyle
  }

  setIndividualTailStyle(tailStyle: 'arrow' | 'dot' | undefined): void {
    this.__tailStyle = tailStyle
  }

  hasIndividualTailStyle(): boolean {
    return this.__tailStyle !== undefined
  }

  getIndividualBalloonScale(): number {
    return this.__balloonScale
  }

  getIndividualConnectedLineThicknessScale(): number {
    return this.__connectedLineThicknessScale
  }

  getIndividualBalloonFontScale(): number {
    return this.__balloonFontScale
  }

  getIndividualPointShapeScale(): number {
    return this.__pointShapeScale
  }

  getIndividualAreaBorderThicknessScale(): number {
    return this.__areaBorderThicknessScale
  }

  setIndividualBalloonScale(scale: number): void {
    this.__balloonScale = scale
  }

  setIndividualConnectedLineThicknessScale(scale: number): void {
    this.__connectedLineThicknessScale = scale
  }

  setIndividualBalloonFontScale(scale: number): void {
    this.__balloonFontScale = scale
  }

  setIndividualPointShapeScale(scale: number): void {
    this.__pointShapeScale = scale
  }

  setIndividualAreaBorderThicknessScale(scale: number): void {
    this.__areaBorderThicknessScale = scale
  }

  hasIndividualBalloonScale(): boolean {
    return this.__balloonScale !== undefined
  }

  hasIndividualConnectedLineThicknessScale(): boolean {
    return this.__connectedLineThicknessScale !== undefined
  }

  hasIndividualBalloonFontScale(): boolean {
    return this.__balloonFontScale !== undefined
  }

  hasIndividualPointShapeScale(): boolean {
    return this.__pointShapeScale !== undefined
  }

  hasIndividualAreaBorderThicknessScale(): boolean {
    return this.__areaBorderThicknessScale !== undefined
  }

  // Interactive 사용자 경험용
  protected __mouseHover: boolean = false
  protected __mouseHoverTempStrokeWidth: number = 0
  protected __originalStrokeWidths: Map<string | number, number> = new Map()

  /**
   * 마우스 Hover 처리
   * @param _ - 마우스/터치 이벤트
   */
  mouseHover(_?: MouseEvent | TouchEvent): void {
    if (isApp()) return
    if (this.canvasEngine.isEntityCreating) return
    if ((this as any).balloon?.head.rect?.isDragging()) return
    if (this.__mouseHover) return

    this.__mouseHover = true

    if (this.isSelected) this.renderer.stage.container().style.cursor = 'move'
    else this.renderer.stage.container().style.cursor = 'pointer'
  }

  /**
   * 마우스 Leave 처리
   * @param _ - 마우스/터치 이벤트
   */
  mouseLeave(_?: MouseEvent | TouchEvent): void {
    if (isApp()) return
    if (!this.__mouseHover) return

    this.__mouseHover = false
    this.renderer.stage.container().style.cursor = 'default'
  }

  // Cloud border 크기 변경 관련
  protected __lastShapeWidth: number = 0
  protected __lastShapeHeight: number = 0

  /**
   * Boundary Center 계산
   */
  getBoundaryCenter(x: number, y: number): { x: number; y: number; zoom: number } {
    const balloon = (this as any).balloon
    const balloonPos = balloon?.getPosition()

    const balloonX = (balloonPos?.x ?? 0) + this.node.x()
    const balloonY = (balloonPos?.y ?? 0) + this.node.y()

    const minX = Math.min(balloonX, x)
    const minY = Math.min(balloonY, y)
    const maxX = Math.max(balloonX, x)
    const maxY = Math.max(balloonY, y)
    const centerX = (minX + maxX) / 2
    const centerY = (minY + maxY) / 2

    const stage = this.renderer.stage
    const stageWidth = stage.width()
    const stageHeight = stage.height()
    const currentScale = stage.scaleX()

    const viewportHalfWidth = stageWidth / 2 / currentScale
    const viewportHalfHeight = stageHeight / 2 / currentScale

    const currentViewportMinX = centerX - viewportHalfWidth
    const currentViewportMaxX = centerX + viewportHalfWidth
    const currentViewportMinY = centerY - viewportHalfHeight
    const currentViewportMaxY = centerY + viewportHalfHeight

    const isOutOfBounds =
      minX < currentViewportMinX ||
      maxX > currentViewportMaxX ||
      minY < currentViewportMinY ||
      maxY > currentViewportMaxY

    if (isOutOfBounds) {
      const requiredWidth = (maxX - minX) * 1.8
      const requiredHeight = (maxY - minY) * 1.8

      const scaleToFitWidth = stageWidth / requiredWidth
      const scaleToFitHeight = stageHeight / requiredHeight
      const optimalScale = Math.min(scaleToFitWidth, scaleToFitHeight)

      const clampedScale = Math.max(Math.min(optimalScale, currentScale * 5), currentScale * 0.1)

      return { x: centerX, y: centerY, zoom: clampedScale }
    }

    return { x: centerX, y: centerY, zoom: currentScale }
  }

  /**
   * Full Boundary Center 계산
   */
  getFullBoundaryCenter(): { x: number; y: number; zoom: number } {
    if (this.__line) {
      const lineX = (this.__line.points()[0] + this.__line.points()[2]) / 2
      const lineY = (this.__line.points()[1] + this.__line.points()[3]) / 2
      return this.getBoundaryCenter(lineX, lineY)
    } else if (isEllipseNode(this.__shape)) {
      return this.getBoundaryCenter(this.node.x(), this.node.y())
    } else if (isRectNode(this.__shape)) {
      const shapeX = this.node.x() + this.__shape.width() / 2
      const shapeY = this.node.y() + this.__shape.height() / 2
      return this.getBoundaryCenter(shapeX, shapeY)
    } else if (this.__brush) {
      const center = this.getBrushCenterPoint()
      return { ...center, zoom: this.renderer.stage.scaleX() }
    } else if (this.isDirection()) {
      if (this.__shape) {
        return {
          x: this.__shape.position().x,
          y: this.__shape.position().y,
          zoom: this.renderer.stage.scaleX(),
        }
      } else if (this.__directionPoints.length >= 2) {
        return {
          x: this.__directionPoints[0],
          y: this.__directionPoints[1],
          zoom: this.renderer.stage.scaleX(),
        }
      }
    } else if (this.isTripod()) {
      const tripodCenter = this.getTripodCenterPoint()
      const centerX = tripodCenter.x + this.node.x()
      const centerY = tripodCenter.y + this.node.y()
      return this.getBoundaryCenter(centerX, centerY)
    }
    throw new Error('getFullBoundaryCenter()을 지원하지 않는 도형입니다.')
  }

  /**
   * 배경 이미지 크기 기반 scale 계산
   */
  protected getImageBasedScale(): number {
    const avgSize = this.canvasEngine.getAverageBackgroundSize()
    if (avgSize === 0) return 1
    return avgSize / SCALE_BASE_SIZE
  }

  /**
   * 배경 이미지 크기가 변경될 때 호출
   */
  onBackgroundSizeChanged(): void {
    if (this.__cloudBorder && this.isRect()) {
      this.createCloudBorder()
    }
  }

  /**
   * 도형에 채우기를 적용하거나 해제
   */
  setShapeFillToggle(value: boolean): void {
    if (!this.isRect() && !this.isEllipse()) return

    const individualSetting = this.getIndividualFillSetting()
    const shouldFill = individualSetting !== undefined ? individualSetting : value

    if (this.__shape && isRectNode(this.__shape)) {
      if (shouldFill) {
        const shapeWidth = this.getShapeWidth()
        const shapeHeight = this.getShapeHeight()
        const patternImage = this.createDiagonalPattern(this.__color, shapeWidth, shapeHeight)
        this.__shape.fillPattern({ image: patternImage })
        this.__shape.fill('')
      } else {
        const emptyCanvas = document.createElement('canvas')
        emptyCanvas.width = 1
        emptyCanvas.height = 1
        this.__shape.fillPattern({ image: emptyCanvas })
        this.__shape.fill('')
      }
    }
  }

  /**
   * 도형의 테두리 스타일 변경
   */
  setShapeStyleToggle(shapeType: 'line' | 'dash' | 'cloud'): void {
    if (!this.isRect()) return

    const individualSetting = this.getIndividualShapeTypeSetting()
    const actualShapeType = individualSetting !== undefined ? individualSetting : shapeType

    if (this.__shape && isRectNode(this.__shape)) {
      const hadCloudBorder = !!this.__cloudBorder

      if (this.__cloudBorder) {
        this.__cloudBorder.destroy()
        this.__cloudBorder = undefined
        this.__shape.stroke(this.__color)
      }

      this.__shape.dash([])
      this.__shape.lineCap('butt')
      this.__shape.lineJoin('miter')
      this.__shape.strokeWidth(this.calculatedAreaLineWidth)

      switch (actualShapeType) {
        case 'dash': {
          const scale = this.getImageBasedScale()
          const dashLength = Math.max(1, DASH_LENGTH * scale)
          const dashGap = Math.max(1, DASH_GAP * scale)
          this.__shape.dash([dashLength, dashGap])
          this.__shape.lineCap('round')
          break
        }
        case 'cloud':
          this.createCloudBorder()
          break
        case 'line':
        default:
          break
      }

      const hasCloudBorderNow = !!this.__cloudBorder
      if (hadCloudBorder !== hasCloudBorderNow) {
        this.updateFillPatternOnResize()
      }
    }
  }

  /**
   * 점 도형의 시각적 형태 변경 (dot: 동그라미 / arrow: 화살표 머리)
   * 화살표 형태/크기는 방향도형(Arrow)과 동일
   * @param dotType - 'dot' 또는 'arrow'
   */
  setDotStyleToggle(dotType: 'dot' | 'arrow' | undefined): void {
    if (!this.isPoint()) return
    const actualDotType: 'dot' | 'arrow' = dotType ?? 'dot'

    // 화살표 도형이 아직 없으면 lazy 생성 (방향도형과 동일한 Arrow)
    if (!this.__pointArrowHead) {
      this.__pointArrowHead = this.renderer.createArrow({
        points: [0, 0, 0, 0],
        pointerLength: this.calculatedArrowHeadSize,
        pointerWidth: this.calculatedArrowHeadSize,
        fill: this.__color,
        stroke: this.__color,
        strokeWidth: 1,
        listening: false,
        perfectDraw: false,
      })
      this.node.add(this.__pointArrowHead)
    }

    if (actualDotType === 'arrow') {
      // 동그라미는 클릭/드래그 hit target으로 살아있어야 하므로 hide 대신 opacity 0 처리
      this.__shape?.opacity(0)
      this.__pointArrowHead.visible(true)
      this.__pointArrowHead.fill(this.__color)
      this.__pointArrowHead.stroke(this.__color)
      this.updatePointArrowHeadGeometry()
    } else {
      this.__shape?.opacity(1)
      this.__pointArrowHead.visible(false)
    }
  }

  /**
   * 화살표 머리의 좌표(방향, 크기)를 balloon 위치/pointShapeScale 기반으로 재계산
   * 방향도형 helper의 head 계산 로직과 동일한 패턴 (tip=라인끝, body=라인 안쪽)
   * 주의: 방향도형은 시각적 진행 방향과 코드의 dir이 반대로 처리되므로,
   * 점도형에서는 line의 진행 방향(balloon → 점)이 아니라 그 반대인 (점 → balloon)을 dir로 사용해야
   * tip이 (0,0)에 위치하고 body가 line 위(balloon 쪽)에 그려짐.
   */
  protected updatePointArrowHeadGeometry(): void {
    if (!this.__pointArrowHead || !this.__pointArrowHead.visible()) return
    if (!this.isPoint()) return

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const balloon = (this as any).balloon
    /* eslint-enable @typescript-eslint/no-explicit-any */
    if (!balloon) return

    const balloonPos = balloon.head.getPosition() as Vector2d
    // dir = (0,0) → balloon 방향 (방향도형 helper의 dir = (second - first) 패턴과 동일하게 처리하되,
    // 점도형의 "first"는 화살표가 있을 (0,0), "second"는 balloon 위치)
    const dx = balloonPos.x
    const dy = balloonPos.y
    const len = Math.sqrt(dx * dx + dy * dy)

    let dirX: number
    let dirY: number
    if (len < 0.001) {
      dirX = 0
      dirY = -1
    } else {
      dirX = dx / len
      dirY = dy / len
    }

    const arrowHeadSize = this.calculatedArrowHeadSize

    // 연결선이 화살표 끝으로 살짝 삐져나오는 것을 가리기 위해
    // 화살표 헤드 전체를 선 진행 방향(=점에서 외부 방향, 즉 -dir)으로 size/2만큼 평행 이동
    const offset = arrowHeadSize / 2
    const headStartX = -dirX * offset
    const headStartY = -dirY * offset

    // 방향도형과 동일한 패턴:
    //   const arrowEndX = headStartX + dir.x * arrowSize
    //   points = [arrowEndX, arrowEndY, headStartX, headStartY]
    const arrowEndX = headStartX + dirX * arrowHeadSize
    const arrowEndY = headStartY + dirY * arrowHeadSize

    this.__pointArrowHead.points([arrowEndX, arrowEndY, headStartX, headStartY])
    this.__pointArrowHead.pointerLength(arrowHeadSize)
    this.__pointArrowHead.pointerWidth(arrowHeadSize)
    this.__pointArrowHead.fill(this.__color)
    this.__pointArrowHead.stroke(this.__color)
  }

  /**
   * Text 위치 업데이트
   */
  protected updateTextPosition(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const balloon = (this as any).balloon
    if (!balloon) return

    // Balloon 모듈 내부에서 자동으로 text 위치 업데이트됨
    balloon.head.updateTextPosition()
  }

  /**
   * Balloon Font Color 설정
   */
  setBalloonFontColor(color: string): void {
    this.__balloonFontColor = color
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const balloon = (this as any).balloon

    if (balloon) {
      balloon.head.text.fill(color)
    }
  }

  /**
   * Balloon Fill Color 설정
   */
  setBalloonFillColor(color: string): void {
    const isEmptyColor =
      !this.canvasEngine.balloonFillOpacity &&
      (color === 'rgba(255, 255, 255, 1)' || color === '#fff' || color === 'white')
    const currentColor = !isEmptyColor ? color : 'rgba(255, 255, 255, 0)'
    this.__balloonFillColor = currentColor
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const balloon = (this as any).balloon

    if (balloon) {
      balloon.head.rect.fill(currentColor)
    }
  }

  /**
   * Tail Style 설정 (TRIPOD 전용)
   */
  setTailStyle(tailStyle: 'arrow' | 'dot'): void {
    if (!this.isTripod()) {
      console.warn('setTailStyle은 TRIPOD 도형에서만 사용할 수 있습니다.')
      return
    }

    this.__tailStyle = tailStyle

    const tripodShapePoints = this.getTripodShapePoints()
    if (tripodShapePoints) {
      tripodShapePoints.tailStyle = tailStyle
      this.setTripodShapePoints(tripodShapePoints)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectionHandler = this as any
    if (selectionHandler.__recreateTripodVisualElements) {
      selectionHandler.__recreateTripodVisualElements()
    }
  }

  /**
   * Balloon Scale 설정
   */
  setBalloonScale(scale: number | null | undefined): void {
    this.__balloonScale =
      scale !== undefined && scale !== null ? scale : this.canvasEngine.forCreateBalloonScale
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const balloon = (this as any).balloon

    if (balloon) {
      if (this.__balloonScale === 0) {
        balloon.head.rect.opacity(0)
        this.updateTextPosition()
        this.setAreaLineWidthDirection() // 벌룬 숨김 시 방향도형 선분 클립 해제
        return
      }
      balloon.head.rect.opacity(1)
      const balloonSize = this.calculatedBalloonSize
      const balloonThickness = this.calculatedBalloonThickness

      balloon.head.rect.width(balloonSize)
      balloon.head.rect.height(balloonSize)
      balloon.head.rect.offsetX(balloonSize / 2)
      balloon.head.rect.offsetY(balloonSize / 2)
      balloon.head.rect.strokeWidth(balloonThickness)
      this.updateTextPosition()
      this.setAreaLineWidthDirection() // 벌룬 크기에 맞춰 방향도형 선분 클립 갱신
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(this as any).updateBalloonPosition?.() // 벌룬 크기에 맞춰 연결선 시작 오프셋 갱신
    }
  }

  /**
   * Connected Line Thickness Scale 설정
   */
  setConnectedLineThicknessScale(scale: number | null | undefined): void {
    this.__connectedLineThicknessScale =
      scale !== undefined && scale !== null
        ? scale
        : this.canvasEngine.forCreateConnectedLineThicknessScale

    if (this.entityType === SHAPES.TRIPOD) {
      const tripodGroup = this.__tripod_transform_group
      if (tripodGroup) {
        tripodGroup.getChildren((node: INode) => {
          if (isLineNode(node)) {
            node.strokeWidth(this.calculatedConnectedLineWidth)
          }
          return false
        })
      }
    } else if (this.entityType === SHAPES.DIRECTION) {
      this.setAreaLineWidthDirection()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const balloon = (this as any).balloon
    if (balloon) {
      balloon.connectedLine.line.strokeWidth(this.calculatedConnectedLineWidth)
    }
  }

  /**
   * Balloon Font Scale 설정
   */
  setBalloonFontScale(scale: number | null | undefined): void {
    this.__balloonFontScale =
      scale !== undefined && scale !== null ? scale : this.canvasEngine.forCreateBalloonFontScale
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const balloon = (this as any).balloon
    if (balloon) {
      if (this.__balloonFontScale === 0) {
        balloon.head.text.visible(false)
        this.updateTextPosition()
        return
      }
      balloon.head.text.visible(true)
      balloon.head.text.fontSize(this.calculatedBalloonFontSize)
      this.updateTextPosition()
    }
  }

  /**
   * Point Shape Scale 설정
   */
  setPointShapeScale(scale: number | null | undefined): void {
    this.__pointShapeScale = scale ? scale : this.canvasEngine.forCreatePointShapeScale

    if (this.entityType === SHAPES.POINT && this.__shape) {
      const newPointSize = this.calculatedPointSize
      if (isEllipseNode(this.__shape)) {
        this.__shape.radiusX(newPointSize)
        this.__shape.radiusY(newPointSize)
      }
      // 화살표 모드일 때 헤드 크기도 비례 갱신
      this.updatePointArrowHeadGeometry()
    } else if (this.entityType === SHAPES.TRIPOD && this.__tripod_transform_group) {
      const newPointSize = this.calculatedPointSize
      const mainAxisHitWidth = 20
      const startPointHitWidth = Math.round(mainAxisHitWidth * 1.2)
      const hitRadius = Math.max(newPointSize + 6, startPointHitWidth / 2 + 2)

      // 화살표 타입이 있는지 확인
      let hasArrowVisuals = false
      this.__tripod_transform_group.getChildren((node: INode) => {
        if (isLineNode(node) && node.name() === 'startPointVisual') {
          hasArrowVisuals = true
        }
        return false
      })

      // 화살표가 있는 경우 전체 시각적 요소 업데이트 (삼각형 크기 재계산)
      if (hasArrowVisuals) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectionHandler = this as any
        if (selectionHandler.__updateTripodVisualElements) {
          selectionHandler.__updateTripodVisualElements()
        }
      } else {
        // 점(dot) 형태인 경우 기존 로직 사용
        this.__tripod_transform_group.getChildren((node: INode) => {
          if (isEllipseNode(node)) {
            if (node.name() === 'startPointVisual') {
              node.radiusX(newPointSize)
              node.radiusY(newPointSize)
            } else if (node.name() === 'startPoint') {
              node.radiusX(hitRadius)
              node.radiusY(hitRadius)
            }
          }
          return false
        })
      }
    } else if (this.entityType === SHAPES.DIRECTION) {
      this.setPointShapeScaleDirection()
    }
  }

  /**
   * Area Border Thickness Scale 설정
   */
  setAreaBorderThicknessScale(scale: number | null | undefined): void {
    this.__areaBorderThicknessScale = scale
      ? scale
      : this.canvasEngine.forCreateAreaBorderThicknessScale

    if (
      this.entityType !== SHAPES.DIRECTION &&
      this.entityType !== SHAPES.TRIPOD &&
      this.entityType === SHAPES.RECT &&
      this.__shape
    ) {
      this.__shape.strokeWidth(this.calculatedAreaLineWidth)
      if (this.__individualShapeTypeSetting === 'cloud' && this.__cloudBorder) {
        this.createCloudBorder()
      }
    }
  }

  /**
   * Direction 도형의 선분에서 벌룬과 겹치는 구간을 잘라내기 위한 클립 정보를 반환합니다.
   * 벌룬이 없거나 숨김(scale 0) 상태면 null을 반환합니다.
   */
  getDirectionBalloonClip(): DirectionBalloonClip | null {
    if (this.entityType !== SHAPES.DIRECTION) return null

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const balloon = (this as any).balloon
    if (!balloon || this.__balloonScale === 0 || balloon.head.rect.opacity() === 0) return null

    const halfSize = balloon.head.rect.width() / 2
    if (halfSize <= 0) return null

    return { halfSize, isCircle: balloon.head.rect.cornerRadius() >= halfSize }
  }

  /**
   * Direction 도형의 Area Line Width 설정
   */
  setAreaLineWidthDirection(): void {
    if (this.entityType === SHAPES.DIRECTION && this.__directionShape) {
      updateDirectionElements(
        this.renderer,
        this.__directionShape,
        this.__directionPoints,
        this.__color,
        1 * this.calculatedConnectedLineWidth,
        this.calculatedArrowHeadSize,
        this.__directionStateManager.originalArrowHead,
        this.getDirectionBalloonClip(),
      )
    }
  }

  /**
   * Direction 도형의 Point Shape Scale 설정
   */
  setPointShapeScaleDirection(): void {
    if (this.entityType === SHAPES.DIRECTION && this.__directionShape) {
      updateDirectionElements(
        this.renderer,
        this.__directionShape,
        this.__directionPoints,
        this.__color,
        1 * this.calculatedConnectedLineWidth,
        this.calculatedArrowHeadSize,
        this.__directionStateManager.originalArrowHead,
        this.getDirectionBalloonClip(),
      )
    }
  }

  /**
   * 사선 패턴 이미지 생성
   */
  protected createDiagonalPattern(color: string, width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas')
    const scale = this.getImageBasedScale()

    const lineWidth = Math.max(0.5, this.canvasEngine.fillPatternLineWidth * scale)
    const spacing = Math.max(2, this.canvasEngine.fillPatternSpacing * scale)

    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')!

    ctx.clearRect(0, 0, width, height)
    ctx.strokeStyle = color
    ctx.lineWidth = lineWidth
    ctx.lineCap = 'round'

    const maxSize = Math.max(width, height)
    const totalRange = maxSize + Math.sqrt(width * width + height * height)

    for (let i = -totalRange; i <= totalRange; i += spacing) {
      ctx.beginPath()
      ctx.moveTo(i, height)
      ctx.lineTo(i + height, 0)
      ctx.stroke()
    }

    return canvas
  }

  /**
   * Fill 패턴 크기 변경 시 업데이트
   */
  protected updateFillPatternOnResize(): void {
    if (!this.isRect() && !this.isEllipse()) return

    const individualFillSetting = this.getIndividualFillSetting()
    if (individualFillSetting !== true) return

    if (this.__shape && (isRectNode(this.__shape) || isEllipseNode(this.__shape))) {
      const shapeWidth = this.getShapeWidth()
      const shapeHeight = this.getShapeHeight()
      const newPatternImage = this.createDiagonalPattern(this.__color, shapeWidth, shapeHeight)
      this.__shape.fillPattern({ image: newPatternImage })
    }
  }

  /**
   * Cloud border rotation 업데이트
   */
  protected updateCloudBorderRotation(): void {
    if (!this.__cloudBorder || !this.__shape) return

    const rect = this.__shape
    const scale = this.getImageBasedScale()
    const arcRadius = Math.max(2, CLOUD_RADIUS * scale)
    const strokeWidth = this.calculatedAreaLineWidth
    const extraPadding = Math.max(arcRadius, strokeWidth) + 2

    this.__cloudBorder.rotation(rect.rotation())
    this.__cloudBorder.x(rect.x())
    this.__cloudBorder.y(rect.y())
    this.__cloudBorder.offsetX(extraPadding)
    this.__cloudBorder.offsetY(extraPadding)
  }

  /**
   * 크기 변경 시 cloud border 업데이트
   */
  updateCloudBorderIfSizeChanged(): void {
    if (!this.__cloudBorder || !this.__shape) return

    const rect = this.__shape
    const currentWidth = rect.width()
    const currentHeight = rect.height()

    if (currentWidth !== this.__lastShapeWidth || currentHeight !== this.__lastShapeHeight) {
      this.createCloudBorder()
      this.__lastShapeWidth = currentWidth
      this.__lastShapeHeight = currentHeight
    } else {
      this.updateCloudBorderRotation()
    }
  }

  /**
   * CAD 스타일 구름 테두리 생성
   */
  protected createCloudBorder(customThickness?: number): void {
    if (!this.__shape || !isRectNode(this.__shape)) return

    this.__shape.stroke('transparent')
    this.__shape.dash([])

    if (this.__cloudBorder) {
      this.__cloudBorder.destroy()
      this.__cloudBorder = undefined
    }

    const rect = this.__shape
    const width = rect.width()
    const height = rect.height()
    const color = this.__color

    const scale = this.getImageBasedScale()
    const arcRadius = Math.max(2, CLOUD_RADIUS * scale)
    const arcSpacing = arcRadius * 2
    const strokeWidth = customThickness || this.calculatedAreaLineWidth

    const extraPadding = Math.max(arcRadius, strokeWidth) + 2
    const cloudWidth = width + extraPadding * 2
    const cloudHeight = height + extraPadding * 2

    const cloudBorder = this.renderer.createPath(
      {
        x: rect.x(),
        y: rect.y(),
        offsetX: extraPadding,
        offsetY: extraPadding,
        width: cloudWidth,
        height: cloudHeight,
        rotation: rect.rotation(),
      },
      (context: IPathContext): void => {
        context.beginPath()
        context.strokeStyle = color
        context.lineWidth = strokeWidth
        context.lineCap = 'round'
        context.lineJoin = 'round'

        const drawX = extraPadding
        const drawY = extraPadding
        const drawWidth = width
        const drawHeight = height

        this.drawCloudEdge(
          context,
          drawX,
          drawY,
          drawX + drawWidth,
          drawY,
          arcRadius,
          arcSpacing,
          'top',
        )
        this.drawCloudEdge(
          context,
          drawX + drawWidth,
          drawY,
          drawX + drawWidth,
          drawY + drawHeight,
          arcRadius,
          arcSpacing,
          'right',
        )
        this.drawCloudEdge(
          context,
          drawX + drawWidth,
          drawY + drawHeight,
          drawX,
          drawY + drawHeight,
          arcRadius,
          arcSpacing,
          'bottom',
        )
        this.drawCloudEdge(
          context,
          drawX,
          drawY + drawHeight,
          drawX,
          drawY,
          arcRadius,
          arcSpacing,
          'left',
        )

        context.stroke()
      },
    )

    if (this.__shape_wrapper) {
      this.__shape_wrapper.add(cloudBorder)
    } else {
      this.node.add(cloudBorder)
    }

    this.__cloudBorder = cloudBorder
    this.__lastShapeWidth = width
    this.__lastShapeHeight = height
  }

  /**
   * 구름 모양의 가장자리 그리기
   */
  protected drawCloudEdge(
    context: IPathContext,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    arcRadius: number,
    spacing: number,
    direction: 'top' | 'right' | 'bottom' | 'left',
  ): void {
    const distance = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2)
    const steps = Math.floor(distance / spacing)

    if (steps === 0) return

    const stepX = (endX - startX) / steps
    const stepY = (endY - startY) / steps

    for (let i = 0; i < steps; i++) {
      const x1 = startX + stepX * i
      const y1 = startY + stepY * i
      const x2 = startX + stepX * (i + 1)
      const y2 = startY + stepY * (i + 1)

      const midX = (x1 + x2) / 2
      const midY = (y1 + y2) / 2

      let offsetX = 0
      let offsetY = 0

      switch (direction) {
        case 'top':
          offsetY = -arcRadius
          break
        case 'bottom':
          offsetY = arcRadius
          break
        case 'left':
          offsetX = -arcRadius
          break
        case 'right':
          offsetX = arcRadius
          break
      }

      const controlX = midX + offsetX
      const controlY = midY + offsetY

      if (i === 0) context.moveTo(x1, y1)
      context.quadraticCurveTo(controlX, controlY, x2, y2)
    }
  }
}
